import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import { makeStyles } from '@material-ui/core/styles';

// The one table used across the dashboard. CustomTable, CustomTablePageNoHeader
// and CustomTableWithoutHeader are thin wrappers around it, so layout, empty
// state and pagination are defined here only.
//
// Pagination has two modes, with the same footer ("Rows per page", "a–b of N",
// page buttons):
// - server: the screen passes `serverSide` (or `count` > 1) with `count` (pages),
//   `page`, `handleChangePage`, and ideally `totalCount`, `pageSize` and
//   `onPageSizeChange(size)`; it fetches each page itself; `alwaysShowPager`
//   keeps the footer (count, page size) even when everything fits on one page;
// - local: otherwise the table pages the rows it was given (25 per page by default).
//   Pass `paginate={false}` for tables that must show every row together, e.g.
//   breakdowns that end with a Total row.
//
// Header (shown when there is a title, search or actions):
// - `title`
// - `search`: { placeholder, onChange, value?, disabled? } renders a search box.
//   Typing is debounced: onChange({ target: { value } }) fires once the user
//   pauses, so a screen fetches once per search, not once per keystroke.
//   Passing `value` lets the screen reset the box (e.g. to '').
// - `actions`: [{ name, icon?, onClick? }] renders contained buttons; items
//   without onClick call `onActionClick(event, index)`.
//
// `loading` shows a thin progress bar under the header and fades the rows,
// instead of a full-screen overlay.

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_EMPTY_TEXT = 'You have not added any data.';
const SEARCH_DELAY_MS = 350;

function SearchBox({ search, classes }) {
  const [term, setTerm] = useState(search.value || '');
  const timer = useRef(null);

  // Follow resets from the screen (it passes value only when it tracks it).
  useEffect(() => {
    if (search.value !== undefined) setTerm(search.value);
  }, [search.value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleChange = (event) => {
    const { value } = event.target;
    setTerm(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search.onChange({ target: { value } }), SEARCH_DELAY_MS);
  };

  return (
    <TextField
      className={classes.search}
      variant='outlined'
      size='small'
      type='search'
      placeholder={search.placeholder || 'Search'}
      disabled={search.disabled}
      value={term}
      onChange={handleChange}
      InputProps={{
        inputProps: { 'aria-label': search.placeholder || 'Search' },
        startAdornment: (
          <InputAdornment position='start'>
            <SearchRoundedIcon className={classes.searchIcon} />
          </InputAdornment>
        ),
      }}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  card: {
    margin: theme.spacing(2),
    overflow: 'hidden',
    border: '1px solid #EAEEF5',
  },
  flush: {
    margin: 0,
  },
  noTopMargin: {
    marginTop: 0,
  },
  squareTop: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: '12px 20px',
    borderBottom: '1px solid #EEF1F6',
  },
  tools: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginLeft: 'auto',
  },
  search: {
    width: 260,
    '& .MuiOutlinedInput-root': { backgroundColor: '#F8FAFC' },
    '& .MuiOutlinedInput-input': { paddingTop: 9, paddingBottom: 9, fontSize: 14 },
  },
  searchIcon: {
    color: '#8A94A6',
    fontSize: 20,
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: '#1F2733',
  },
  container: {
    overflowX: 'auto',
    transition: 'opacity 0.15s ease',
  },
  fading: {
    opacity: 0.55,
  },
  progressSlot: {
    height: 3,
    marginBottom: -3,
    position: 'relative',
    zIndex: 3,
  },
  progress: {
    height: 3,
  },
  empty: {
    textAlign: 'center',
    color: '#8A94A6',
    fontSize: 15,
    padding: '40px 16px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: '10px 16px',
    borderTop: '1px solid #EEF1F6',
  },
  footerCentered: {
    justifyContent: 'center',
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontSize: 13,
    color: '#5B6472',
  },
  pageSize: {
    fontSize: 13,
  },
}));

export default function DataTable({
  tableHead,
  itemComponent,
  noDataText,
  title,
  actions,
  onActionClick,
  search,
  count,
  page,
  handleChangePage,
  paginate = true,
  flush,
  noTopMargin,
  squareTop,
  bare,
  loading,
  serverSide,
  totalCount,
  pageSize: serverPageSize,
  onPageSizeChange,
  pageSizeOptions,
  alwaysShowPager,
}) {
  const classes = useStyles();
  const rows = React.Children.toArray(itemComponent);
  const columns = tableHead || [];
  const serverPaged = Boolean(serverSide) || Number(count) > 1;
  const localPaged = paginate && !serverPaged;

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [localPage, setLocalPage] = useState(1);
  const localPages = Math.max(1, Math.ceil(rows.length / pageSize));

  // Keep the page in range when rows shrink (search, delete).
  useEffect(() => {
    if (localPage > localPages) setLocalPage(localPages);
  }, [localPage, localPages]);

  const visibleRows = localPaged
    ? rows.slice((localPage - 1) * pageSize, localPage * pageSize)
    : rows;
  // One footer for both modes: "Rows per page", "a–b of N" and the page buttons.
  const sizesWith = (size) => [...new Set([...(pageSizeOptions || PAGE_SIZES), size])]
    .filter(Boolean).sort((a, b) => a - b);
  let pager = { show: false };
  if (serverPaged) {
    const size = Number(serverPageSize) || rows.length || 1;
    const current = Number(page) || 1;
    const total = totalCount === undefined ? undefined : Number(totalCount) || 0;
    const sizes = sizesWith(Number(serverPageSize));
    pager = {
      size,
      sizes,
      current,
      total,
      pages: Math.max(1, Math.ceil(Number(count) || 1)),
      first: total ? (current - 1) * size + 1 : 0,
      last: total ? Math.min(current * size, total) : 0,
      onPage: handleChangePage,
      onSize: onPageSizeChange,
    };
    pager.hasSummary = Boolean(pager.onSize || total !== undefined);
    pager.show = Boolean(alwaysShowPager && total) || pager.pages > 1
      || (total !== undefined && total > sizes[0]);
  } else if (localPaged) {
    pager = {
      size: pageSize,
      sizes: sizesWith(pageSize),
      current: localPage,
      total: rows.length,
      pages: localPages,
      first: rows.length ? (localPage - 1) * pageSize + 1 : 0,
      last: Math.min(localPage * pageSize, rows.length),
      onPage: (e, newPage) => setLocalPage(newPage),
      onSize: (size) => { setPageSize(size); setLocalPage(1); },
      hasSummary: true,
      show: rows.length > DEFAULT_PAGE_SIZE || pageSize !== DEFAULT_PAGE_SIZE,
    };
  }
  const cardClass = [
    classes.card,
    flush && classes.flush,
    noTopMargin && classes.noTopMargin,
    squareTop && classes.squareTop,
  ].filter(Boolean).join(' ');
  const visibleActions = (actions || []).filter(Boolean);
  const showHeader = Boolean(title || search || visibleActions.length);
  // `bare` renders just the table and pager, for use inside another card.
  const Wrapper = bare ? 'div' : Paper;

  return (
    <Wrapper className={bare ? 'dt-bare' : cardClass}>
      {showHeader ? (
        <div className={classes.header}>
          {title ? <Typography className={classes.title} component='div'>{title}</Typography> : null}
          <div className={classes.tools}>
            {search ? <SearchBox search={search} classes={classes} /> : null}
            {visibleActions.map((item, index) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.name}
                  variant='contained'
                  color='primary'
                  startIcon={Icon ? <Icon /> : null}
                  onClick={(event) => {
                    if (item.onClick) item.onClick(event);
                    else if (onActionClick) onActionClick(event, index);
                  }}
                >
                  {item.name}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Fixed-height slot so the table doesn't jump when loading starts/stops. */}
      <div className={classes.progressSlot}>
        {loading ? <LinearProgress className={classes.progress} /> : null}
      </div>
      <TableContainer className={`${classes.container} ${loading ? classes.fading : ''}`}>
        <Table stickyHeader>
          {columns.length ? (
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {visibleRows.length ? visibleRows : (
              <TableRow>
                <TableCell className={classes.empty} colSpan={columns.length || 1}>
                  {loading ? 'Loading…' : (noDataText || DEFAULT_EMPTY_TEXT)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pager.show ? (
        <div className={`${classes.footer} ${pager.hasSummary ? '' : classes.footerCentered}`}>
          {pager.hasSummary ? (
            <div className={classes.summary}>
              {pager.onSize ? (
                <>
                  <span>Rows per page</span>
                  <Select
                    className={classes.pageSize}
                    value={pager.size}
                    disableUnderline
                    onChange={(e) => pager.onSize(e.target.value)}
                  >
                    {pager.sizes.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                  </Select>
                </>
              ) : null}
              {pager.total !== undefined ? (
                <span>{`${pager.first}–${pager.last} of ${pager.total}`}</span>
              ) : null}
            </div>
          ) : null}
          {pager.pages > 1 ? (
            <Pagination count={pager.pages} page={pager.current} onChange={pager.onPage} />
          ) : null}
        </div>
      ) : null}
    </Wrapper>
  );
}
